// ═══════════════════════════════════════════════════════════════
// error-grouping — turning a list of crash reports into a list of
// BUGS.
//
// Kept free of any Firebase import so it can be exercised directly:
// the moment a module touches firebase.js it needs Vite's import.meta
// env to exist, and logic you can't run outside a browser is logic
// that gets shipped untested.
// ═══════════════════════════════════════════════════════════════
import { toDate } from './job-helpers.js';

export function isHandled(report) {
  return !!report?.handledAt;
}

// Older reports predate the stored fingerprint. Rebuild it from what
// they do have rather than dropping them into a giant "undefined"
// bucket.
export function reportKey(r) {
  return r?.fingerprint || `${r?.context || '?'}::${r?.name || ''}::${(r?.message || '').slice(0, 200)}`;
}

// Collapse reports into one row per distinct bug.
//
// A group counts as handled only if EVERY report in it is handled —
// so a bug you marked done last week reappears the moment it happens
// again, which is the behaviour you want from a triage list.
export function groupReports(reports) {
  const groups = new Map();
  (Array.isArray(reports) ? reports : []).forEach((r) => {
    const key = reportKey(r);
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        sample: r,
        count: 0,
        reports: [],
        users: new Set(),
        versions: new Set(),
        envs: new Set(),
        paths: new Set(),
        firstSeen: null,
        lastSeen: null,
        unhandled: 0,
      };
      groups.set(key, g);
    }
    g.count += 1;
    g.reports.push(r);
    if (r.userId) g.users.add(r.userId);
    if (r.appVersion) g.versions.add(r.appVersion);
    if (r.env) g.envs.add(r.env);
    if (r.path) g.paths.add(r.path);
    if (!isHandled(r)) g.unhandled += 1;

    const at = toDate(r.createdAt);
    if (at) {
      if (!g.firstSeen || at < g.firstSeen) g.firstSeen = at;
      if (!g.lastSeen  || at > g.lastSeen)  g.lastSeen = at;
      // The newest report is the most useful sample: it carries the
      // stack from the build people are actually running.
      const sampleAt = toDate(g.sample.createdAt);
      if (!sampleAt || at > sampleAt) g.sample = r;
    }
  });

  return [...groups.values()]
    .map((g) => ({
      ...g,
      users: [...g.users],
      versions: [...g.versions].sort(),
      envs: [...g.envs].sort(),
      paths: [...g.paths],
      handled: g.unhandled === 0,
    }))
    .sort((a, b) => (b.lastSeen?.getTime() ?? 0) - (a.lastSeen?.getTime() ?? 0));
}
